/**
 * react-query hooks for the AV-03 corpus-quality dashboard (CZNA-330).
 *
 * Closure-capture `ky` (MEMORY: useOkapiKy returns an unstable instance — never
 * a query dep). All hooks are read-only; starting a run reuses
 * `useStartRevalidate` from {@link useRevalidateJob}.
 */
import { useQuery } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { getLatestRun, getRuns, getRunDetail, getTrend, getLatestCorpusChecks, getCorpusCheckFindings, getRulesetStatus, } from './qualityService';
/** Latest completed run; `null` when none recorded yet. */
export var useLatestRun = function () {
    var ky = useOkapiKy();
    return useQuery(['quality-latest-run'], function () { return getLatestRun(ky); }, { refetchOnWindowFocus: false });
};
/** Paginated run history (newest first). */
export var useRuns = function (offset, limit) {
    if (offset === void 0) { offset = 0; }
    if (limit === void 0) { limit = 20; }
    var ky = useOkapiKy();
    return useQuery(['quality-runs', offset, limit], function () { return getRuns(ky, { offset: offset, limit: limit }); }, { keepPreviousData: true, refetchOnWindowFocus: false });
};
/** Full detail of one run. Disabled until an id is provided. */
export var useRunDetail = function (id) {
    var ky = useOkapiKy();
    return useQuery(['quality-run-detail', id], function () { return getRunDetail(ky, id); }, { enabled: !!id, refetchOnWindowFocus: false });
};
/** Oldest-first trend points. */
export var useTrend = function (limit) {
    if (limit === void 0) { limit = 24; }
    var ky = useOkapiKy();
    return useQuery(['quality-trend', limit], function () { return getTrend(ky, limit); }, { refetchOnWindowFocus: false });
};
/** CZNA-1620 — did the validation rules change after the last full revalidation. */
export var useRulesetStatus = function () {
    var ky = useOkapiKy();
    return useQuery(['quality-ruleset-status'], function () { return getRulesetStatus(ky); }, { refetchOnWindowFocus: false });
};
/** CZNA-1217 — last stored corpus-check run; `null` when the checks never ran. */
export var useLatestCorpusChecks = function () {
    var ky = useOkapiKy();
    return useQuery(['corpus-checks-latest'], function () { return getLatestCorpusChecks(ky); }, { refetchOnWindowFocus: false });
};
/** CZNA-1217 — one page of a check's violations. Disabled until the run is known. */
export var useCorpusCheckFindings = function (jobId, checkCode, offset, limit, recordType) {
    if (offset === void 0) { offset = 0; }
    if (limit === void 0) { limit = 50; }
    if (recordType === void 0) { recordType = null; }
    var ky = useOkapiKy();
    return useQuery(['corpus-check-findings', jobId, checkCode, offset, limit, recordType], function () { return getCorpusCheckFindings(ky, jobId, checkCode, { offset: offset, limit: limit, recordType: recordType }); }, { enabled: !!jobId && !!checkCode, keepPreviousData: true, refetchOnWindowFocus: false });
};
